import { makeJobRequest, postJob } from './JobsApi';

/**
 * @typedef {'folding' | 'maplight' | 'gnina_scoring' | 'reinvent'
 *  | 'gnina_docking' | 'library_screen'} JobTypeOptions
 * @type {{
 *    FOLDING: "folding",
 *    MAPLIGHT: "maplight",
 *    GNINA_SCORING: "gnina_scoring",
 *    REINVENT: "reinvent",
 *    GNINA_DOCKING: "gnina_docking",
 *    LIBRARY_SCREEN: "library_screen"
 * }}
 */
export const JobTypes = {
    FOLDING: 'folding',
    MAPLIGHT: 'maplight',
    GNINA_SCORING: 'gnina_scoring',
    REINVENT: 'reinvent',
    GNINA_DOCKING: 'gnina_docking',
    LIBRARY_SCREEN: 'library_screen',
};

/**
 * @typedef {'structure' | 'compound' | 'screening' | 'workspace'} JobResultDestinationOptions
 * @type {{
 *    STRUCTURE: "structure",
 *    COMPOUND: "compound",
 *    SCREENING: "screening",
 *    WORKSPACE: "workspace"
 * }}
 */
export const JobResultDestinations = {
    STRUCTURE: 'structure',
    COMPOUND: 'compound',
    SCREENING: 'screening',
    WORKSPACE: 'workspace',
};

/**
 * @typedef {'succeeded' | 'running' | 'failed' | 'starting' | 'unknown'} JobStatusOptions
 * @type {{
 *    SUCCEEDED: "succeeded",
 *    RUNNING: "running",
 *    FAILED: "failed",
 *    STARTING: "starting",
 *    UNKNOWN: "unknown"
 * }}
 */
export const JobStatus = {
    SUCCEEDED: 'succeeded',
    RUNNING: 'running',
    FAILED: 'failed',
    STARTING: 'starting',
    UNKNOWN: 'unknown',
};

/**
 * @typedef {object} JobConstructorData
 * @property {string} [jobId] - The job id assigned by the job manager.
 * @property {string} [created] - ISO timestamp the job was created.
 * @property {string} [delivered] - ISO timestamp results were delivered.
 * @property {string} [done] - ISO timestamp the job finished.
 * @property {string} [modified] - ISO timestamp the job was last modified.
 * @property {object} [requestmeta] - Job-type specific request metadata.
 * @property {object} [stash] - Caller-supplied data round-tripped with the job.
 * @property {object} [jobrequest] - The original request payload.
 * @property {object} [results] - The job results packet, if available.
 */

/**
 * @typedef {object} IntegrateResultsReturn
 * @property {object} [results] - The (possibly transformed) results packet to
 *   pass back to the caller. Shape is job-type specific.
 * @property {string} [error] - A user-facing error string, if integration failed.
 */

/**
 * @typedef {object} IntegrateResultsOptions
 * @property {Array<import('BMapsModel').Compound|import('BMapsModel').MapCase>} [items]
 *   - The workspace items the results should be applied to, when applicable.
 * @property {object} [loadOptions] - Structure/compound load options.
 * @property {object} [dataConnection] - The data connection to load results into.
 */

/**
 * Abstract base class for all job types managed by jobman.
 *
 * A concrete job type extends this class and:
 *  - sets the static descriptors (`jobTypeId`, `resultDestination`,
 *    `jobTypeName`, `jobName`, and optionally `propertyScope`, `idField`,
 *    `scopedPropertyMetadata`);
 *  - implements {@link JobDefinition#integrateResults} to apply the job's
 *    results to the workspace / redux state;
 *  - optionally overrides {@link JobDefinition#getRequestMeta} and the static
 *    {@link JobDefinition.getDataInfo} to customize the request envelope and
 *    the results-table column metadata.
 *
 * Instances are created by the jobs redux layer
 * (see `BMapsRedux/jobs/thunks` `loadResult`) via `new JobClass({ jobId })`.
 *
 * @abstract
 */
export class JobDefinition {
    /**
     * Stable job-type identifier; one of {@link JobTypes}. Also used as the
     * default property scope when `propertyScope` is not set.
     * @type {JobTypeOptions}
     */
    static jobTypeId = undefined;

    /**
     * Where this job's results are surfaced in the UI.
     * @type {JobResultDestinationOptions}
     */
    static resultDestination = undefined;

    /**
     * Scoped property metadata contributed by this job type. Aggregated by
     * `jobRegistry.getJobsPropertyMetadata` and registered on the workspace.
     * @type {object}
     */
    static scopedPropertyMetadata = {};

    /**
     * The scope under which this job's properties are stored on a compound.
     * Defaults to `jobTypeId` (see constructor). Override only when a job must
     * write into another job type's scope (e.g. Gnina Docking → Gnina Scoring).
     * @type {string|undefined}
     */
    static propertyScope = undefined;

    /**
     * The result-row field used as the unique row id in results tables.
     * Consumers fall back to `'id'` when this is undefined.
     * @type {string|undefined}
     */
    static idField = undefined;

    /** Human-readable job type name, e.g. "Gnina Scoring". @type {string} */
    static jobTypeName = undefined;

    /** Human-readable job instance name, e.g. "Gnina Scoring Job". @type {string} */
    static jobName = undefined;

    /**
     * Copies the static descriptors onto the instance and seeds instance
     * state from the supplied job data.
     * @param {JobConstructorData} data
     */
    constructor({
        jobId,
        created,
        delivered,
        done,
        modified,
        requestmeta,
        stash,
        jobrequest,
        results,
    }) {
        // Copy static properties from Job Class into the instance
        this.jobTypeId = this.constructor.jobTypeId;
        this.propertyScope = this.constructor.propertyScope ?? this.constructor.jobTypeId;
        this.resultDestination = this.constructor.resultDestination;
        this.dataInfo = this.constructor.dataInfo;
        this.jobTypeName = this.constructor.jobTypeName;
        this.jobName = this.constructor.jobName;

        this.jobid = jobId;
        this.jobCreationError = undefined;

        this.updateFrom({
            created,
            delivered,
            done,
            modified,
            requestmeta,
            stash,
            jobrequest,
            results,
        });
    }

    /**
     * Merges a job data packet into the instance, mapping incoming server
     * keys to the instance's internal property names.
     * @param {Partial<JobConstructorData> & { jobid?: string, jobtype?: string,
     *   state?: import('./JobDefinition').JobStatusOptions }} [data]
     * @returns {this}
     */
    updateFrom(data = {}) {
        const keyMap = {
            jobid: 'jobid',
            jobtype: 'jobTypeId',
            created: 'created',
            delivered: 'delivered',
            done: 'done',
            modified: 'modified',
            requestmeta: 'requestMeta',
            stash: 'stash',
            jobrequest: 'jobRequest',
            results: 'results',
            state: 'state',
        };

        Object.entries(keyMap).forEach(([incomingKey, internalKey]) => {
            if (Object.prototype.hasOwnProperty.call(data, incomingKey)) {
                this[internalKey] = data[incomingKey];
            }
        });

        return this;
    }

    /** @returns {JobTypeOptions} Alias of `jobTypeId` for redux/job-state shape parity. */
    get jobtype() { return this.jobTypeId; }

    /**
     * Builds the results-table column metadata by flattening
     * `scopedPropertyMetadata`. Job types whose results-table columns differ
     * from their scoped properties (e.g. Gnina Docking) override this.
     * @returns {Object<string, object>} map of property name → column metadata
     */
    static getDataInfo() {
        const result = {};
        const scopedMeta = this.scopedPropertyMetadata || {};
        for (const entityMeta of Object.values(scopedMeta)) {
            for (const group of Object.values(entityMeta.childGroups || {})) {
                for (const [key, prop] of Object.entries(group.properties || {})) {
                    result[key] = prop.metadata || {};
                }
            }
        }
        return result;
    }

    /** @returns {Object<string, object>} {@link JobDefinition.getDataInfo} as a getter. */
    static get dataInfo() { return this.getDataInfo(); }

    /** @returns {string} Human-readable job type name. */
    static getJobTypeName() { return this.jobTypeName; }

    /** @returns {string} Human-readable job instance name. */
    static getJobName() { return this.jobName; }

    /** @returns {object} This job type's scoped property metadata (or `{}`). */
    static getScopedPropertyMetadata() {
        return this.scopedPropertyMetadata || {};
    }

    /**
     * Wraps a request payload in the job-manager envelope, attaching the
     * request metadata produced by {@link JobDefinition#getRequestMeta}.
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} [stash] - Caller data round-tripped with the job.
     * @returns {object} The job-manager request envelope.
     */
    prepareRequest(jobrequest, stash={}) {
        return makeJobRequest({
            jobtype: this.jobTypeId,
            jobrequest,
            stash,
            requestmeta: this.getRequestMeta(jobrequest, stash),
        });
    }

    /**
     * Produces the `requestmeta` stored alongside the job. Override to expose
     * additional job-type specific metadata (see e.g. FoldingJob).
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} _stash - Caller data round-tripped with the job (unused
     *   in the base implementation).
     * @returns {object} request metadata
     */
    getRequestMeta(jobrequest, _stash) {
        return {
            jobName: jobrequest?.jobName,
        };
    }

    /**
     * Submits the job to the job manager and records the response on this
     * instance.
     * @param {object} jobrequest - The job-type specific request payload.
     * @param {object} [stash] - Caller data round-tripped with the job.
     * @returns {Promise<import('./JobsApi').postJobResponse>} the post-job response
     */
    async sendJobCreationRequest(jobrequest, stash={}) {
        const requestData = this.prepareRequest(jobrequest, stash);
        const response = await postJob(requestData);
        this.updatePostJobResponse(response);
        return response;
    }

    /**
     * Applies the result of {@link JobDefinition#sendJobCreationRequest} to
     * this instance, recording any creation error.
     * @param {{ results?: object, error?: string }} response
     * @returns {void}
     */
    updatePostJobResponse(response) {
        const { results, error } = response;
        if (results) {
            this.updateFrom(results);
        }
        this.jobCreationError = error;
    }

    /**
     * Applies a completed job's results to the application: loading
     * structures, setting compound properties, updating local job state, etc.
     *
     * This is the primary extension point of a job type and MUST be
     * implemented by every concrete subclass.
     *
     * @abstract
     * @param {object} _job - The redux job-state record for this job.
     * @param {object|string} _results - The results packet from the job
     *   manager (may be a JSON string that the implementation parses).
     * @param {IntegrateResultsOptions} [_options]
     * @returns {Promise<IntegrateResultsReturn>}
     */
    async integrateResults(_job, _results, _options) {
        throw new Error(
            `${this.constructor.name}.integrateResults is not implemented`
        );
    }
}
